import BezierCurve from 'bezier-js'; // eslint-disable-line import/extensions
import MathHelper from './MathHelper';
import { GetLH, GetRH, GetRelLH, GetRelRH, ChangeKnotPointToAbsolute } from './KnotHelper';
import { PIXEL_PER_CM } from '../constants/units';

const ONE_HANDLE_INTERPOLATION = 0.4;
const TWO_HANDLES_INTERPOLATION = 0.3;

export function convertKnotToSequentialAbsolutePoints(sK, eK) {
  // All the bezier points are absolutes
  const startKnot = sK;
  const endKnot = eK;
  const rH = GetRelRH(startKnot) || 0;
  const lH = GetRelLH(endKnot) || 0;
  return [startKnot.x, startKnot.y, startKnot.x + rH.x, startKnot.y + rH.y, endKnot.x + lH.x, endKnot.y + lH.y, endKnot.x, endKnot.y];
}

export function convertBezierPointsToBezierCurve(one, two, three, four, five, six, seven, eight) {
  return new BezierCurve(one, two, three, four, five, six, seven, eight);
}

export function convertKnotPairToBezier(k1, k2) {
  return new BezierCurve(convertKnotToSequentialAbsolutePoints(k1, k2));
}

export function convertPointPairToBezierJS(p1, p2) {
  const pointOne = p1;
  const pointTwo = p2;
  const dx = (pointTwo.x - pointOne.x) / 3;
  const dy = (pointTwo.y - pointOne.y) / 3;

  return new BezierCurve(pointOne.x, pointOne.y, pointOne.x + dx, pointOne.y + dy,
    pointOne.x + (2 * dx), pointOne.y + (2 * dy), pointTwo.x, pointTwo.y);
}

export function getOffset(bezier, d) {
  let offset;
  try {
    offset = bezier.offset(-d);
  } catch (err) {
    /**
     * sometimes seams generate conflict curves
     * This is just a temporary hack while we
     * monitor this problem with Raven
     * error: 'cannot scale this curve. Try reducing it first'
     */
    const bezierReduced = bezier.reduce();
    offset = bezierReduced[0].offset(-d);

    console.error(err);
  }

  return roundBeziers(offset);
}

export function convertSegmentIntoBezierCurves(knots, getPoints = false, interpolate = false, scale = 1) {
  const beziers = [];
  knots.slice(0, knots.length - 1).forEach((knot, idx) => {
    beziers.push(getSegmentBezierCurve(knots, idx, getPoints, interpolate, scale));
  });

  return beziers;
}

export function getSegmentBezierCurve(knots, bezierIdx, getPoints = false, interpolate = false, scale = 1) {
  const startKnot = knots[bezierIdx];
  const endKnot = knots[bezierIdx + 1];

  if (interpolate) {
    interpolateKnots(startKnot, endKnot, scale);
  }

  const points = convertKnotToSequentialAbsolutePoints(startKnot, endKnot);
  return getPoints ? points : new BezierCurve(points);
}

export function interpolateKnots(sK, eK, scale = 1) {
  const startKnot = sK;
  const endKnot = eK;

  if (startKnot.rHI && endKnot.lHI) {
    startKnot.rH = MathHelper.scalarMultiplicationPoint(MathHelper.interpolatePoints(startKnot, endKnot, TWO_HANDLES_INTERPOLATION), scale);
    endKnot.lH = MathHelper.scalarMultiplicationPoint(MathHelper.interpolatePoints(endKnot, startKnot, TWO_HANDLES_INTERPOLATION), scale);
  } else if (endKnot.lHI) {
    endKnot.lH = MathHelper.scalarMultiplicationPoint(MathHelper.interpolatePoints(endKnot, MathHelper.addPoints(startKnot, GetRH(startKnot)), ONE_HANDLE_INTERPOLATION), scale);
  } else if (startKnot.rHI) {
    startKnot.rH = MathHelper.scalarMultiplicationPoint(MathHelper.interpolatePoints(startKnot, MathHelper.addPoints(endKnot, GetLH(endKnot)), ONE_HANDLE_INTERPOLATION), scale);
  }
}

export function convertBezierJSToSequentialPoints(bezier, returnAsString = false) {
  const { points } = bezier;
  const p1 = [points[1].x, points[1].y];
  const p2 = [points[2].x, points[2].y];
  const p3 = [points[3].x, points[3].y];
  return !returnAsString ? [...p1, ...p2, ...p3] : `${p1.join(' ')}, ${p2.join(' ')}, ${p3.join(' ')}`;
}

/**
 * Returns array or string with: right handle of last knot, lH of next knot, (x,y) of next knot.
 */
export function convertKnotToSequentialPoints(lastKnot, nextKnot, returnAsString = false) {
  const { rhAx, rhAy } = ChangeKnotPointToAbsolute(lastKnot);
  const { x, y, lhAx, lhAy } = ChangeKnotPointToAbsolute(nextKnot);
  const lRH = [rhAx, rhAy];
  const nLH = [lhAx, lhAy];
  const nP = [x, y];

  return !returnAsString ? [...lRH, ...nLH, ...nP] : `${lRH.join(' ')}, ${nLH.join(' ')}, ${nP.join(' ')}`;
}

export function getLength(bezier) {
  return bezier.length();
}

export function getT(bezier, t) {
  return bezier.get(t);
}

export function getDistanceT(bezier, t) {
  const splitLeft = split(bezier, t).left;

  return splitLeft.length();
}

export function split(bezier, t) {
  return bezier.split(t);
}

export function getNormalT(bezier, t) {
  return bezier.normal(t);
}

export function getNormalTLength(bezier, t, length) {
  const tPoint = getT(bezier, t);
  const normal = getNormalT(bezier, t);

  return { x: tPoint.x + (length * normal.x), y: tPoint.y + (length * normal.y) };
}

export function getNormalDistance(bezier, distance) {
  let startT = 0.001;
  let endT = 1;
  let midT = startT + ((endT - startT) / 2);

  while (startT <= endT) {
    midT = startT + ((endT - startT) / 2);
    const d = split(bezier, midT).left.length();

    if (d < distance) {
      startT = midT + 0.001;
    }

    if (d > distance) {
      endT = midT - 0.001;
    }

    if (MathHelper.equals(d, distance)) {
      return getNormalT(bezier, midT);
    }
  }

  return getNormalT(bezier, midT);
}

export function getTagentT(bezier, t) {
  return bezier.derivative(t);
}

export function getTagentTLength(bezier, t, length) {
  const tPoint = bezier.get(t);
  let tagent = getTagentT(bezier, t);
  tagent = MathHelper.getNormalizedVector(tagent);

  return { x: tPoint.x + (length * tagent.x), y: tPoint.y + (length * tagent.y) };
}

// TODO: Check getNormalDistance method (sergio)
export function getPointPair(bezier, distance, seam = null, length = PIXEL_PER_CM) {
  const distancePoint = getPointDistance(bezier, distance);
  const normal = getNormalDistance(bezier, distance);
  const separation = seam ? -seam : length;

  if (distancePoint && normal) {
    const normalPoint = { x: distancePoint.x + (separation * normal.x), y: distancePoint.y + (separation * normal.y) };
    return {
      p1: distancePoint,
      p2: normalPoint,
    };
  }

  return null;
}

export function getPointDistance(bezier, distance) {
  let startT = 0.001;
  let endT = 1;
  let midT = startT + ((endT - startT) / 2);

  while (startT <= endT) {
    midT = startT + ((endT - startT) / 2);
    const d = split(bezier, midT).left.length();

    if (d < distance) {
      startT = midT + 0.001;
    }

    if (d > distance) {
      endT = midT - 0.001;
    }

    if (MathHelper.equals(d, distance)) {
      return { ...getT(bezier, midT), t: midT };
    }
  }

  return { ...getT(bezier, midT), t: midT };
}

export function getBounds(bezier) {
  const bounds = bezier.bbox();
  const { x, y } = bounds;

  return { x: x.min, y: y.min, w: x.max - x.min, h: y.max - y.min };
}

export function getLastIntersection(bezier, beziers) {
  for (let i = beziers.length - 1; i >= 0; i -= 1) {
    if (MathHelper.areBoundsIntersected(getBounds(bezier), getBounds(beziers[i]))) {
      const intersections = bezier.intersects(beziers[i]);

      if (intersections && intersections[0]) {
        return {
          bezierIdx: i,
          t: intersections[0].split('/').map((value) => parseFloat(value)),
        };
      }
    }
  }

  return null;
}

export function roundBeziers(beziers) {
  return beziers.map((bezier) => roundBezier(bezier));
}

export function roundBezier(bezier) {
  const b = bezier;
  b.points = b.points.map((point) => point);
  return b;
}

export function intersectCurveByLine(curve, line, returnFirstIntersection = false) {
  const intersects = curve.intersects(line);

  if (typeof intersects === 'undefined' || !intersects || !intersects.length) {
    return {
      flag: false,
      t: -1,
    };
  }

  return {
    flag: true,
    t: returnFirstIntersection ? intersects[0] : intersects[intersects.length - 1],
  };
}

export function getReflectedBeziers(beziers, axisStart, axisEnd) {
  return beziers.map((bezier) => ({ ...bezier, points: bezier.points.map((point) => MathHelper.reflectPoint(point, axisStart, axisEnd)) }));
}

// a kind of bisection method implementation
export function obtainTRespectPoint(beziers, point0) {
  let a = 0;
  let b = 1;
  const e = 0.0005;
  const delta = 0.0001;
  let x1;
  let x2;
  let y1;
  let y2;
  let point1;
  let point2;
  let L;
  do {
    x1 = ((a + b) / 2) - (delta / 2);
    x2 = ((a + b) / 2) + (delta / 2);
    point1 = beziers.get(x1);
    point2 = beziers.get(x2);
    y1 = Math.sqrt(((point0.x - point1.x) ** 2) + ((point0.y - point1.y) ** 2));
    y2 = Math.sqrt(((point0.x - point2.x) ** 2) + ((point0.y - point2.y) ** 2));
    if (y1 > y2) {
      a = x1;
    } else {
      b = x2;
    }
    L = b - a;
  } while (L > e);
  return (b + a) / 2;
}

/**
 * this will create a bezier curve with the format of "getGradeBezeirsForKnots" placed
 * in PieceHelper
 * @param {Array<object>} gradeSegment
 * @return {Array<Bezier>} an array with Beziers
 */
export function createGradeBazierCurve(gradeSegment) {
  return gradeSegment.map((gradeKnot) => {
    const { points } = gradeKnot;
    return new BezierCurve(
      points[0].x,
      points[0].y,
      points[1].x,
      points[1].y,
      points[2].x,
      points[2].y,
      points[3].x,
      points[3].y
    );
  });
}

export function createBezierCurveSeveralKnots(segment) {
  const bezierKnots = [];
  bezierKnots.push(segment[0]);
  bezierKnots.push(MathHelper.addPoints(segment[0], segment[0].rH));
  for (let i = 1; i < segment.length - 1; i += 1) {
    bezierKnots.push(MathHelper.addPoints(segment[i], segment[i].lH));
    bezierKnots.push(MathHelper.addPoints(segment[i], segment[i].rH));
  }
  bezierKnots.push(MathHelper.addPoints(segment[segment.length - 1], segment[segment.length - 1].lH));
  bezierKnots.push(segment[segment.length - 1]);
  const bezierSegment = new BezierCurve(bezierKnots);
  return bezierSegment;
}
